import { ActionTree } from "vuex";
import { IPropertyEditState } from "../property-states.store";
import propertyKorfineTypesStore from "./property-korfine-types.store";
import { DataHelper } from "../../../../utils/data-helper";
import { KorfineRequestModel } from "../../../../models/property/property-korfine.model";
import { formatPhoneNumberForKorfine, isEmptyKorfinePerson } from "../../../../utils/util";
import { buildKorfineContractDocuments } from "../../../../utils/korfine-documents";
import { korfineRegistrationNumber } from "../../../../utils/korfine-person";

const korfineActions: ActionTree<IPropertyEditState, {}> = <ActionTree<IPropertyEditState, {}>>{
    [propertyKorfineTypesStore.Get.PUBLISH_KORFINE]: ({getters, commit}, param: number) => {
        return getters.service['getPublishKorfine'](param)
            .then((data) => {
                commit(propertyKorfineTypesStore.Set.KORFINE_REQUEST_INFO, data);
                commit(propertyKorfineTypesStore.Set.KORFINE_ASSIGN_VALUE, { ObjectId: param });
                
                return Promise.resolve(data);
                }).catch(error => Promise.reject(error))
    },
    publishKorfine: ({ getters, commit }) => {
      let payload = DataHelper.deepClone(getters.korfine) as KorfineRequestModel;

      // Extra attachments (WinApp parity): one flat list of storage paths; the API reads the bytes.
      payload.Contract.Document = buildKorfineContractDocuments(payload.Attachments);
      delete payload.Attachments;
      delete payload.AttachmentsLoading;

      // The deposit is optional (WinApp sends 0,00); the API field is a non-nullable decimal.
      payload.Contract.Amount = payload.Contract.Amount ?? 0;

      // WinApp (UCKorfine.GetRequestData) always creates a draft deposit contract; the form has no field for
      // either, so without these Korfine receives a contract with no status and no contract type.
      payload.Contract.Status = "Draft";
      payload.Contract.TypeOfContract = "Deposit";

      // Tax-residence parity with WinApp (OMSEVEN-10678): the owner tax fields
      // are hidden and the owner is always sent as a Belgian tax resident,
      // otherwise the API builds an empty TaxResidences entry Korfine rejects.
      const owner = payload.Contract.Owner;
      if (owner) {
        owner.TaxResidenceBelgium = true;
        owner.TaxResidenceCountries = null;
        owner.TaxResidenceNumbers = null;
      }

      // The tenant checkbox defaults to checked; only an explicit uncheck sends
      // a foreign tax residence (country + identification number).
      const tenant = payload.Contract.Tenant;
      if (tenant) {
        tenant.TaxResidenceBelgium = tenant.TaxResidenceBelgium ?? true;
        if (tenant.TaxResidenceBelgium) {
          tenant.TaxResidenceCountries = null;
          tenant.TaxResidenceNumbers = null;
        }
      }

      // Partner info is optional (matching WinApp). When no partner relation was
      // filled in, send `null` instead of an empty object so Korfine doesn't get
      // a blank partner.
      if (payload.Contract.Owner && isEmptyKorfinePerson(payload.Contract.Owner.Partner)) {
        payload.Contract.Owner.Partner = null;
      }
      if (payload.Contract.Tenant && isEmptyKorfinePerson(payload.Contract.Tenant.Partner)) {
        payload.Contract.Tenant.Partner = null;
      }

      // Company number goes to Korfine's RegistrationNumber with its country code, as WinApp (UCKorfine.GetRegistrationNumber).
      [payload.Contract.Owner?.Person, payload.Contract.Owner?.Partner, payload.Contract.Tenant?.Person, payload.Contract.Tenant?.Partner]
        .filter(Boolean)
        .forEach(person => { person.RegistrationNumber = korfineRegistrationNumber(person.RegistrationNumber); });

      if (payload.Contract.Owner.Person.Cellphone) {
        payload.Contract.Owner.Person.Cellphone = formatPhoneNumberForKorfine(payload.Contract.Owner.Person.Cellphone);
      }
      if (payload.Contract.Owner.Person.Phone) {
        payload.Contract.Owner.Person.Phone = formatPhoneNumberForKorfine(payload.Contract.Owner.Person.Phone);
      }
      if (payload.Contract.Owner.Partner?.Cellphone) {
        payload.Contract.Owner.Partner.Cellphone = formatPhoneNumberForKorfine(payload.Contract.Owner.Partner.Cellphone);
      }
      if (payload.Contract.Owner.Partner?.Phone) {
        payload.Contract.Owner.Partner.Phone = formatPhoneNumberForKorfine(payload.Contract.Owner.Partner.Phone);
      }
      if (payload.Contract.Tenant.Person.Cellphone) {
        payload.Contract.Tenant.Person.Cellphone = formatPhoneNumberForKorfine(payload.Contract.Tenant.Person.Cellphone);
      }
      if (payload.Contract.Tenant.Person.Phone) {
        payload.Contract.Tenant.Person.Phone = formatPhoneNumberForKorfine(payload.Contract.Tenant.Person.Phone);
      }
      if (payload.Contract.Tenant.Partner?.Cellphone) {
        payload.Contract.Tenant.Partner.Cellphone = formatPhoneNumberForKorfine(payload.Contract.Tenant.Partner.Cellphone);
      }
      if (payload.Contract.Tenant.Partner?.Phone) {
        payload.Contract.Tenant.Partner.Phone = formatPhoneNumberForKorfine(payload.Contract.Tenant.Partner.Phone);
      }

      return getters.service.publishKorfine(payload)
      .then((data) => { 
        return Promise.resolve(data);
      }).catch(error => Promise.reject(error))
  }

}
export default korfineActions;